import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../app/AuthContext";
import { STELLAR_STAGES } from "../stellar-engine";
import { useStellar } from "./StellarContext";
import "../styles/stellar.css";

const navigation = [
  ["", "恒星核心", "CORE"],
  ["archive", "观测档案", "ARCH"],
  ["settings", "设置", "SET"],
] as const;

export function StellarLayout() {
  const auth = useAuth();
  const game = useStellar();
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const stage = game.state?.stage;
  const stellarView = location.pathname.split("/")[2] || "core";

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  async function signOut() {
    game.flushPending();
    setLogoutError(null);
    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch (cause) {
      setLogoutError(cause instanceof Error ? cause.message : "退出失败。 ");
    }
  }

  const marker = gameStateMarker(game.status, game.state);
  return (
    <div
      className="stellar-shell"
      data-game-state={marker}
      data-stellar-view={stellarView}
      data-stellar-stage={stage ?? "uninitialized"}
      data-state-version={game.stateVersion}
      data-generation={game.state?.meta.generation ?? 0}
      data-reduced-motion={String(game.state?.settings.reducedMotion ?? false)}
    >
      <a className="stellar-skip-link" href="#stellar-main">跳到恒星观测台</a>
      <OfflineReport />

      <header className="stellar-mobile-header">
        <StellarMark />
        <div><b>星核纪元</b><span>{game.state?.star ? STELLAR_STAGES[game.state.stage].name : "星云演化核心"}</span></div>
        <button type="button" onClick={() => { game.flushPending(); navigate("/game"); }} aria-label="返回游戏大厅">大厅</button>
      </header>

      <aside className="stellar-side-nav">
        <div className="stellar-brand"><StellarMark /><div><b>星核纪元</b><span>STELLAR GENESIS</span></div></div>
        <div className="stellar-generation-status">
          <span>当前观测</span>
          <strong>第 {game.state?.meta.generation ?? 1} 代</strong>
          <small>{game.state?.star ? STELLAR_STAGES[game.state.stage].name : "等待质量路线"}</small>
        </div>
        <nav aria-label="星核纪元主要导航">
          {navigation.map(([path, label, code]) => (
            <NavLink key={path || "core"} end={!path} to={path ? `/stellar/${path}` : "/stellar"}>
              <span aria-hidden="true">{code}</span><b>{label}</b>
            </NavLink>
          ))}
        </nav>
        <div className="stellar-save-state" role="status" aria-live="polite">
          <span className={game.syncing ? "syncing" : "saved"} aria-hidden="true" />
          <div><b>{game.syncing ? "正在写入数据库" : "进度已同步"}</b><small>{game.queuedPulses ? `${game.queuedPulses} 次脉冲待确认` : `状态版本 ${Math.max(0, game.stateVersion)}`}</small></div>
        </div>
        <div className="stellar-account">
          <span>{auth.user?.displayName}</span><small>{auth.user?.email}</small>
          <button className="button ghost" type="button" onClick={() => { game.flushPending(); navigate("/game"); }}>返回游戏大厅</button>
          <button className="button ghost" type="button" onClick={() => void signOut()}>退出登录</button>
          {logoutError && <small className="danger-text" role="alert">{logoutError}</small>}
        </div>
      </aside>

      <main id="stellar-main" className="stellar-main" tabIndex={-1}>
        <StellarFeedback />
        {game.status === "loading" && !game.state ? (
          <div className="stellar-loading" role="status"><span /><h1>正在唤醒星云演化核心</h1><p>同步账号并结算离线观测…</p></div>
        ) : game.status === "error" && !game.state ? (
          <div className="stellar-loading error" role="alert"><span /><h1>星核进度载入失败</h1><p>{game.error}</p><button className="button primary" onClick={() => void game.refresh()}>重新连接</button></div>
        ) : <Outlet />}
      </main>

      <nav className="stellar-bottom-nav" aria-label="星核纪元移动端导航">
        {navigation.map(([path, label, code]) => (
          <NavLink key={path || "core"} end={!path} to={path ? `/stellar/${path}` : "/stellar"}>
            <span aria-hidden="true">{code}</span><b>{label}</b>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function StellarFeedback() {
  const game = useStellar();
  if (game.error) return <div className="stellar-feedback danger" role="alert"><span>{game.error}</span><button type="button" onClick={game.clearFeedback}>关闭</button></div>;
  if (game.notice.length) return <div className="stellar-feedback success" role="status"><span>{game.notice.join(" · ")}</span><button type="button" onClick={game.clearFeedback}>关闭</button></div>;
  return null;
}

function OfflineReport() {
  const game = useStellar();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const report = game.offline;
  const shouldShow = Boolean(report && (report.elapsedRealSeconds >= 30 || report.messages.length));
  useEffect(() => {
    const dialog = dialogRef.current;
    if (shouldShow && dialog && !dialog.open) dialog.showModal();
  }, [shouldShow]);
  if (!report || !shouldShow) return null;
  return (
    <dialog ref={dialogRef} className="stellar-return-dialog" aria-labelledby="stellar-return-heading" onClose={game.dismissOffline} data-offline-report>
      <form method="dialog">
        <header><div><p className="stellar-kicker">OBSERVATORY RESUMED</p><h2 id="stellar-return-heading">离线观测报告</h2></div><span>{formatElapsed(report.elapsedRealSeconds)}</span></header>
        <p>自治仪器只推进到当前阶段边界；到达边界后会等待你点击确认，不会自动跨越阶段。</p>
        <dl>
          <div><dt>有效模拟</dt><dd>{formatElapsed(report.simulatedSeconds)}</dd></div>
          <div><dt>阶段进度</dt><dd>+{formatNumber(report.progressGained)}</dd></div>
          <div><dt>见解</dt><dd>+{formatNumber(report.insightGained)}</dd></div>
          <div><dt>星光记录</dt><dd>+{formatNumber(report.starlightGained)}</dd></div>
        </dl>
        {report.messages.length > 0 && <ul>{report.messages.map((message, index) => <li key={`${message}-${index}`}>{message}</li>)}</ul>}
        <button className="button primary full" value="acknowledged">查看当前恒星</button>
      </form>
    </dialog>
  );
}

function StellarMark() {
  return <span className="stellar-mark" aria-hidden="true"><i /><i /><i /></span>;
}

function gameStateMarker(
  status: string,
  state: ReturnType<typeof useStellar>["state"],
) {
  if (!state) return status;
  if (!state.tutorial.bootstrapComplete) return "opening";
  if (!state.star) return "mass-selection";
  if (!state.tutorial.firstPulseComplete && state.stage !== "remnant") return "opening";
  if (state.stage === "remnant") return "overall-ending";
  return "active-play";
}

function formatElapsed(seconds: number) {
  if (seconds < 60) return `${Math.round(seconds)} 秒`;
  if (seconds < 3_600) return `${Math.round(seconds / 60)} 分钟`;
  if (seconds < 86_400) return `${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 1 }).format(seconds / 3_600)} 小时`;
  return `${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 1 }).format(seconds / 86_400)} 天`;
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 1 }).format(value);
}
